import {type InfiniteData, useInfiniteQuery} from "@tanstack/react-query";
import {queryHttp} from "../../../query/queryHttp";
import {environment} from "../../../env";
import {CHAT_CONFIG} from "../services/ChatConfiguration";
import {sessionsListUrl} from "../services/chatAgentApiUrls";
import type {SessionListResponse} from "../types/chatTypes";

export const SESSION_PAGE_SIZE = 50;

export const SESSION_LIST_QUERY_KEY_BASE = "/chat-agent/sessions/list";

/** Per-databag cache key (kept url-shaped to satisfy UrlQueryKey-style keys). */
export function sessionListQueryKey(databagId: number) {
    return [`${SESSION_LIST_QUERY_KEY_BASE}?databag_id=${databagId}`] as const;
}

type SessionListQueryKey = ReturnType<typeof sessionListQueryKey>;

/** Paginated sessions for a databag (`GET …/sessions/list`). */
export function useSessionListQuery(databagId: number | undefined) {
    return useInfiniteQuery<
        SessionListResponse,
        Error,
        InfiniteData<SessionListResponse>,
        SessionListQueryKey,
        number
    >({
        queryKey: sessionListQueryKey(databagId ?? 0),
        queryFn: async ({pageParam, signal}) => {
            if (environment.isTestChat || !CHAT_CONFIG.API_URL || !databagId) {
                return {items: [], total: 0, limit: SESSION_PAGE_SIZE, offset: pageParam};
            }
            const {data} = await queryHttp.get<SessionListResponse>(
                sessionsListUrl(databagId, SESSION_PAGE_SIZE, pageParam),
                {signal},
            );
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const nextOffset = lastPage.offset + lastPage.items.length;
            return nextOffset < lastPage.total ? nextOffset : undefined;
        },
        enabled: Boolean(databagId),
    });
}
